import {
  X,
  MousePointer2,
  Hand,
  ZoomIn,
  Eye,
  Circle,
  CircleDot,
  Maximize2,
  Pointer,
} from 'lucide-react';
import { Sheet } from '@ai-tutor/ui';

interface GraphHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** A phone: the touch gestures, in a bottom sheet. */
  touch?: boolean;
}

interface HelpItem {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface HelpSection {
  heading: string;
  items: HelpItem[];
}

const SECTIONS: HelpSection[] = [
  {
    heading: 'Navigate',
    items: [
      {
        icon: <MousePointer2 className='h-4 w-4' />,
        title: 'Pan',
        description: 'Drag empty space to move the canvas.',
      },
      {
        icon: <ZoomIn className='h-4 w-4' />,
        title: 'Zoom',
        description:
          'Scroll or pinch. Labels appear once you’re zoomed in enough to read them.',
      },
    ],
  },
  {
    heading: 'Read the graph',
    items: [
      {
        icon: <Eye className='h-4 w-4' />,
        title: 'Hover to focus',
        description:
          'Hovering a concept dims everything except it and its direct neighbours.',
      },
      {
        icon: <Circle className='h-4 w-4' />,
        title: 'Indigo is what you have learned',
        description:
          'Deep indigo means learned. A paler indigo means it is starting to fade. Grey means you have not studied it yet.',
      },
      {
        icon: <CircleDot className='h-4 w-4' />,
        title: 'Amber is worth a review',
        description:
          'Amber marks the concepts it has been long enough since you saw to be worth going back to.',
      },
      {
        icon: <Circle className='h-4 w-4' />,
        title: 'A hollow ring is paused',
        description:
          'Paused concepts are left out of your reviews. Turn on Show paused to see them on the map, and resume any of them from its panel.',
      },
    ],
  },
];

const ORPHAN_ITEM: HelpItem = {
  icon: <Circle className='h-4 w-4' />,
  title: 'Concepts with no links',
  description:
    'A concept whose source lesson is gone is drawn lighter still. It keeps its review history; it is just no longer attached to anything.',
};

/** The same guide for a finger: no hover, no wheel, no drag-to-pin. */
const TOUCH_SECTIONS: HelpSection[] = [
  {
    heading: 'Navigate',
    items: [
      {
        icon: <Hand className='h-4 w-4' />,
        title: 'Move',
        description: 'Drag with one finger to move around the map.',
      },
      {
        icon: <ZoomIn className='h-4 w-4' />,
        title: 'Zoom',
        description:
          'Pinch. Names appear once you’re close enough to read them.',
      },
      {
        icon: <Maximize2 className='h-4 w-4' />,
        title: 'See it all',
        description: 'The fit button brings the whole map back into view.',
      },
    ],
  },
  {
    heading: 'Read the graph',
    items: [
      {
        icon: <Pointer className='h-4 w-4' />,
        title: 'Tap a concept',
        description:
          'It and its direct links light up, with their names. Open it for the details.',
      },
      ORPHAN_ITEM,
    ],
  },
];

function HelpSections({ sections }: { sections: HelpSection[] }) {
  return (
    <div className='space-y-5'>
      {sections.map(section => (
        <section key={section.heading}>
          <h3 className='mb-2 text-xs font-medium text-muted-foreground'>
            {section.heading}
          </h3>
          <ul className='space-y-2.5'>
            {section.items.map(item => (
              <li key={item.title} className='flex items-start gap-3'>
                <div className='mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:bg-muted dark:text-primary'>
                  {item.icon}
                </div>
                <div className='min-w-0'>
                  <p className='text-sm font-medium text-foreground'>
                    {item.title}
                  </p>
                  <p className='mt-0.5 text-xs leading-relaxed text-muted-foreground'>
                    {item.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function GraphHelpModal({
  isOpen,
  onClose,
  touch = false,
}: GraphHelpModalProps) {
  if (touch) {
    return (
      <Sheet isOpen={isOpen} onClose={onClose} title='Graph guide' size='auto'>
        <div className='px-4 pb-[max(1rem,env(safe-area-inset-bottom))]'>
          <HelpSections sections={TOUCH_SECTIONS} />
          <button
            type='button'
            onClick={onClose}
            className='mt-5 h-11 w-full rounded-xl bg-primary text-sm font-semibold text-primary-foreground'
          >
            Got it
          </button>
        </div>
      </Sheet>
    );
  }
  if (!isOpen) return null;

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm'
      onClick={onClose}
    >
      {/* `bg-card`, not `bg-background`. In light those are two different
          colours (card is #F4F6FF and background is #DAE1F8, a mid lavender),
          so a panel on `bg-background` floated a dull grey-blue sheet over
          the page while every other raised surface in the app sits on card.
          The surface contract in `globals.css` is explicit about the order:
          card is above ground is above background. A modal is a raised
          surface. */}
      <div
        className='max-h-[80vh] w-full max-w-md overflow-hidden rounded-xl border border-border bg-card shadow-2xl'
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className='flex items-center justify-between border-b border-border px-5 py-4'>
          <div>
            <h2 className='text-base font-medium text-foreground'>
              Graph guide
            </h2>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              Tips for getting around your concept map.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label='Close help'
            className='rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
          >
            <X className='h-4 w-4' />
          </button>
        </div>

        {/* Sections */}
        <div className='max-h-[60vh] overflow-y-auto px-5 py-4'>
          <HelpSections sections={SECTIONS} />
        </div>

        {/* Footer */}
        <div className='border-t border-border px-5 py-3'>
          <button
            onClick={onClose}
            className='w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90'
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
